import { Input, Spin, Tooltip } from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { MutableRefObject, MouseEvent, RefObject } from 'react';
import { useCallback } from 'react';

import Loader from 'components/molecules/Loader';
import { colorConstants } from 'config/colorConstants';

import PackArchitectureTableEmptyState from './PackArchitectureTableEmptyState';

import {
    StyledTable,
    TableContainer,
    TableContent,
    TableFooter,
    TableLoaderOverlay,
    TableSearchButton,
    TableSearchGroup,
    TableToolbar,
} from './PackArchitectureTable.styles';
import { PackArchitectureTableKpiCheckbox } from './PackArchitectureTableKpiCheckbox';
import { usePackArchitectureTable } from './usePackArchitectureTable';

type PackArchitectureTableProps = {
    tableContainerRef?: MutableRefObject<HTMLDivElement | null>;
    tableWrapperRef?: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
};

const PackArchitectureTable = ({
    tableContainerRef,
    tableWrapperRef: externalTableWrapperRef,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
}: PackArchitectureTableProps = {}) => {
    const {
        tooltip,
        tableTitle,
        kpiOptions,
        selectedKpis,
        search,
        setSearch,
        submitSearch,
        toggleKpi,
        tableWrapperRef,
        antColumns,
        filteredRows,
        showTable,
        isTableEmpty,
        emptyStateTitle,
        emptyStateDescription,
        footerLabel,
        kpiTickCount,
        showTableLoader,
        isAppendingPages,
        tableLoaderMinHeight,
        tableEmptyStateMinHeight,
        getRowClassName,
        handleRowClick,
        clearHighlight,
    } = usePackArchitectureTable({
        tableWrapperRef: externalTableWrapperRef,
        isSyncingTableFromChartRef,
        suppressChartToTableSyncRef,
    });

    const setTableContainerNode = useCallback(
        (node: HTMLDivElement | null) => {
            if (tableContainerRef) {
                tableContainerRef.current = node;
            }
        },
        [tableContainerRef],
    );

    const handleToolbarMouseDown = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const target = event.target as HTMLElement;
            if (target.closest('.toolbar-checkboxes') || target.closest('.toolbar-search')) {
                return;
            }
            clearHighlight();
        },
        [clearHighlight],
    );

    if (!showTable) return null;

    return (
        <TableContainer ref={setTableContainerNode}>
            <TableToolbar onMouseDown={handleToolbarMouseDown}>
                <div className="toolbar-left">
                    <div className="toolbar-title">
                        <span className="toolbar-title-text">{tableTitle}</span>
                        <Tooltip
                            placement="right"
                            color={colorConstants.blackAlt1}
                            title={tooltip || undefined}
                        >
                            <InfoCircleOutlined
                                className="toolbar-title-icon"
                                aria-label={tooltip ? 'Table information' : undefined}
                            />
                        </Tooltip>
                    </div>

                    <div className="toolbar-checkboxes">
                        {kpiOptions.map((option) => (
                            <PackArchitectureTableKpiCheckbox
                                key={option.id}
                                option={option}
                                isChecked={selectedKpis.includes(option.id)}
                                selectedKpiCount={selectedKpis.length}
                                onToggle={toggleKpi}
                            />
                        ))}
                    </div>
                </div>

                <div className="toolbar-search">
                    <TableSearchGroup>
                        <Input
                            className="table-search-input"
                            bordered={false}
                            placeholder="Search"
                            value={search}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearch(value);
                                if (value === '') {
                                    submitSearch('');
                                }
                            }}
                            onPressEnter={() => submitSearch()}
                            allowClear
                            aria-label="Search table rows"
                        />
                        <TableSearchButton type="button" onClick={() => submitSearch()} aria-label="Submit table search">
                            <SearchOutlined />
                        </TableSearchButton>
                    </TableSearchGroup>
                </div>
            </TableToolbar>

            <TableContent ref={tableWrapperRef as RefObject<HTMLDivElement>}>
                {showTableLoader ? (
                    <TableLoaderOverlay $minHeight={tableLoaderMinHeight}>
                        <Loader />
                    </TableLoaderOverlay>
                ) : (isTableEmpty || filteredRows.length == 0) ? (
                    <PackArchitectureTableEmptyState
                        title={emptyStateTitle}
                        description={emptyStateDescription}
                        minHeight={tableEmptyStateMinHeight}
                    />
                ) : (
                    <StyledTable
                        bordered
                        loading={
                            isAppendingPages
                                ? { spinning: true, indicator: <Spin size="small" /> }
                                : false
                        }
                        $kpiTickCount={kpiTickCount}
                        columns={antColumns}
                        dataSource={filteredRows}
                        rowKey="key"
                        rowClassName={getRowClassName}
                        onRow={(record) => ({
                            onClick: () => handleRowClick(record),
                        })}
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content', y: tableLoaderMinHeight }}
                    />
                )}
            </TableContent>

            {(!isTableEmpty || isAppendingPages) && <TableFooter>{footerLabel}</TableFooter>}
        </TableContainer>
    );
};

export default PackArchitectureTable;
